import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getCategories } from '../../services/cataloge';
import { IProductDetail, PRODUCT_STATUS } from '../../types/products';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { isTruncated, truncateText } from '../../utils/truncateText';
import { Checkbox } from 'antd';
import useCatalogueState from '../../state/useCatalogueState';
import BordTooltip from '../BordTooltip/BordTooltip';
import CountryFlagsSection from './components/CountryFlagsSection/CountryFlagsSection';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import './ProductCard.scss';

interface IProduct {
  product: IProductDetail;
}

const ProductCard: FC<IProduct> = ({ product }: IProduct) => {
  const { isOnlyDevsAutomates } = useOnlyDevelopers();
  const { push } = useHistory();
  const { setListOfCheckedProducts, listOfCheckedProducts, setShowPricingForm } = useCatalogueState();
  const { uid, category, imageUrl, commercialName, brand, techRefence, availableCountries, productId } = product;

  const [enableContainerClick, setEnableContainerClick] = useState(true);
  const [checked, setChecked] = useState(false);

  const handleCheckboxChange = (checked: boolean, productId: number) => {
    setChecked(checked);
    if (checked) {
      setListOfCheckedProducts([...listOfCheckedProducts, productId] as number[]);
    } else {
      setListOfCheckedProducts(listOfCheckedProducts?.filter(item => item !== productId));
    }
  };

  const characteristicsPills =
    techRefence &&
    Object.entries(techRefence)?.map(([key, value], i) => {
      return (
        <div key={`pill-${i}-${key}`} className="productPill characteristicPill">
          {truncateText(value, 7)}
          {isTruncated(value, 8) && <div className="pillHover">{value}</div>}
        </div>
      );
    });

  const countriesFlags =
    availableCountries &&
    availableCountries?.length > 0 &&
    availableCountries?.map((country, i) => {
      const inactiveCountryStyle = country?.active && country?.inStock ? '' : 'inactive';
      return (
        <div
          key={`countryFlag-${i}-${country?.countryCode}`}
          className={`countryFlag ${inactiveCountryStyle}`}
          style={{ backgroundImage: `url(${country?.flag})` }}
        />
      );
    });

  const displayedFlags =
    countriesFlags && countriesFlags?.length > 11 ? [...countriesFlags]?.slice(0, 10) : countriesFlags || undefined;
  const numberOfAdditionalCountries = Math.max(0, availableCountries?.length - 10);

  const renderCountryFlags = (countries: typeof availableCountries, maxDisplay: number = 5) => {
    if (!countries || countries?.length === 0)
      return { displayedFlags: [], numberOfAdditional: 0, additionalCountries: [] };

    const flags = countries.map((country, i) => {
      const inactiveCountryStyle = !country?.active && country?.status === PRODUCT_STATUS.OFF ? 'inactive' : '';
      return (
        <BordTooltip
          key={`countryFlag-${i}-${country?.countryCode}`}
          position="bottomLeft"
          message={country?.countryName}
        >
          <div className={`countryFlag ${inactiveCountryStyle}`} style={{ backgroundImage: `url(${country?.flag})` }} />
        </BordTooltip>
      );
    });

    const displayedFlags = flags?.length > maxDisplay ? flags?.slice(0, maxDisplay) : flags;
    const numberOfAdditional = Math.max(0, countries?.length - maxDisplay);
    const additionalCountries = numberOfAdditional > 0 ? countries.slice(maxDisplay) : [];

    return {
      displayedFlags: displayedFlags,
      numberOfAdditional,
      additionalCountries
    };
  };

  const getCountryFlagsData = (filterType: PRODUCT_STATUS, maxDisplay: number = 5) => {
    if (!availableCountries) return { displayedFlags: [], numberOfAdditional: 0, additionalCountries: [] };

    let filteredCountries: typeof availableCountries = [];

    switch (filterType) {
      case PRODUCT_STATUS.ON:
        filteredCountries = availableCountries.filter(
          country => country?.active && country?.inStock && country?.status === PRODUCT_STATUS.ON
        );
        break;
      case PRODUCT_STATUS.TO_QUOTE:
        filteredCountries = availableCountries.filter(
          country => !country?.active && country?.status === PRODUCT_STATUS.TO_QUOTE
        );
        break;
      case PRODUCT_STATUS.OFF:
        filteredCountries = availableCountries.filter(
          country => !country?.active && country?.status === PRODUCT_STATUS.OFF
        );
        break;
    }

    return renderCountryFlags(filteredCountries, maxDisplay);
  };

  const storeFlagsData = getCountryFlagsData(PRODUCT_STATUS.ON, 5);
  const quoteFlagsData = getCountryFlagsData(PRODUCT_STATUS.TO_QUOTE, 5);
  const inactiveFlagsData = getCountryFlagsData(PRODUCT_STATUS.OFF, 5);
  const asignCategory = async () => {
    try {
      const dataCategories = getCategories();
      return dataCategories;
    } catch (error) {
      displayErrorNotification();
    }
  };

  const viewDetails = async (category: string) => {
    setListOfCheckedProducts([]);
    setShowPricingForm(false);
    const listCategories = await asignCategory();
    if (listCategories) {
      const selectCategory = listCategories?.find(item => item?.name === category);
      if (selectCategory) {
        push(`/catalog/create-product/${selectCategory?.id}?productId=${product?.productId}`);
      }
    }
  };

  return (
    <>
      <div
        className="productCard"
        onClick={() => {
          if (enableContainerClick) {
            viewDetails(category);
          }
        }}
      >
        <section className="skuSection">
          <Checkbox
            onMouseEnter={() => {
              setEnableContainerClick(false);
            }}
            onMouseLeave={() => {
              setEnableContainerClick(true);
            }}
            onChange={evt => {
              handleCheckboxChange(evt.target.checked, productId);
            }}
            checked={checked}
          />
          <div className="sku">{uid}</div>
        </section>
        <section className="categorySection">{category}</section>
        <section className="photoSection">
          <img src={imageUrl} alt="Imagen del producto" />
        </section>
        <section className="brandSection">{brand}</section>
        <section className="nameSection">{commercialName}</section>
        <section className="characteristicsSection">{characteristicsPills}</section>
        <section className="availableCountriesSection">
          {isOnlyDevsAutomates ? (
            <CountryFlagsSection
              storeFlagsData={storeFlagsData}
              quoteFlagsData={quoteFlagsData}
              inactiveFlagsData={inactiveFlagsData}
            />
          ) : (
            <>
              {displayedFlags}
              {numberOfAdditionalCountries > 1 && (
                <div className="countryFlag additionalCountries">+{numberOfAdditionalCountries}</div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
};

export default ProductCard;
